import Text from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'

import { Localized } from '../../localization/react'
import {
  CardType,
  hasActivityBasedParticipationRules,
  splitUsagesByActivityScope,
  UnlimitedUsage,
  UsageLimit,
  UsageLimitType,
} from '../../typings/Product'

interface Props {
  usages: Array<UsageLimit>
  unlimitedUsages: Array<UnlimitedUsage>
  cardType: CardType
}

interface BookingRulesProps {
  bookingRules: Array<UsageLimit>
}

interface ActivityUsageInformationProps {
  totalUsage: UsageLimit | undefined
  cardType: CardType
}

const CardUsagesInformation = ({ usages, unlimitedUsages, cardType }: Props) => {
  const { activityUsages, overallUsages } = splitUsagesByActivityScope(usages)
  const hasActivityBasedRules = hasActivityBasedParticipationRules(activityUsages, unlimitedUsages)

  const totalLimitTypeUsages =
    cardType !== CardType.CARD_TYPE_EVENT_TIME
      ? overallUsages.filter((usage) => usage.type === UsageLimitType.USAGE_LIMIT_TYPE_TOTAL)
      : []
  const repeatingAndPerBookingLimitTypeUsages = overallUsages.filter(
    (usage) => usage.type !== UsageLimitType.USAGE_LIMIT_TYPE_TOTAL,
  )

  if (hasActivityBasedRules) {
    return <ActivityUsageInformation totalUsage={overallUsages[0]} cardType={cardType} />
  }

  return (
    <>
      {totalLimitTypeUsages.map((usage, index) => (
        <div key={`${usage.type}-${index}`}>
          <Text size="small">
            <Localized id="units-header" params={{ type: usage.unit as any }} />
          </Text>
          <Text>
            <Localized
              id="units"
              params={{ limit: usage.usageLimit, unit: usage.unit as any, currency: usage.currency || '' }}
            />
          </Text>
        </div>
      ))}
      {repeatingAndPerBookingLimitTypeUsages.length > 0 && (
        <BookingRules bookingRules={repeatingAndPerBookingLimitTypeUsages} />
      )}
    </>
  )
}

const ActivityUsageInformation = ({ totalUsage, cardType }: ActivityUsageInformationProps) => {
  if (cardType === CardType.CARD_TYPE_EVENT_TIME || !totalUsage) {
    return (
      <div>
        <Text size="small">
          <Localized id="booking-rules-header" />
        </Text>
        <Text>
          <Localized id="card-activity-based-access" />
        </Text>
      </div>
    )
  }

  return (
    <div>
      <Text size="small">
        <Localized id="units-header" params={{ type: totalUsage.unit }} />
      </Text>
      <Text>
        <Localized id="card-block-activity-based-access" params={{ limit: totalUsage.usageLimit }} />
      </Text>
    </div>
  )
}

const BookingRules = ({ bookingRules }: BookingRulesProps) => (
  <div>
    <Text size="small">
      <Localized id="booking-rules-header" />
    </Text>
    {bookingRules.map((usage, index) => {
      switch (usage.type) {
        case UsageLimitType.USAGE_LIMIT_TYPE_REPEATING:
          return (
            <Text key={`${usage.type}-${index}`}>
              <Localized
                id="product-booking-rules"
                params={{
                  intervalValue: usage.usageLimit,
                  intervalUnit: usage.unit,
                  interval: (
                    <Localized
                      id="interval"
                      params={{
                        intervalValue: usage.repeating!.interval.value,
                        intervalType: usage.repeating!.interval.type,
                      }}
                    />
                  ),
                }}
              />
            </Text>
          )
        case UsageLimitType.USAGE_LIMIT_TYPE_PER_BOOKING:
          return (
            <Text key={`${usage.type}-${index}`}>
              <Localized id="booking-limitation" params={{ limit: usage.usageLimit }} />
            </Text>
          )
        case UsageLimitType.USAGE_LIMIT_TYPE_TOTAL:
          // This never happens, it's just here to satisfy the type validation
          return null
        default:
          return assertNever(usage.type)
      }
    })}
  </div>
)

export default CardUsagesInformation
